import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { MutationOptions } from '@tanstack/react-query'
import { cosmicSupportedCurrencies } from '@taxbit-private/cosmic-localization'

import {
  AccountingStandard,
  CostBasisCarryover,
  CostTracking,
  FetchAccountingSettingsConfigResponse,
  FetchAnchorSwapsResponse,
  InventorySeparation,
  MissingCostBasis,
} from 'api/types/accounting-settings/accountingSettingsApiTypes'
import useApi from 'api/useApi'
import BookType from 'constants/BookType'
import Permission from 'constants/Permissions'
import { useUser } from 'contexts/UserContext'
import { useOptimisticPendingChanges } from 'hooks/data/gc-dags/useOptimisticPendingChanges'
import { QueryKey } from 'hooks/data/queryKeys'
import { useOrgQueryKey } from 'hooks/utils/useOrgQueryKey'

export const ACCOUNTING_CONFIG_OPTIONS = {
  accounting_standard: {
    [AccountingStandard.IfrsRevaluation]: 'IFRSRevaluation',
    [AccountingStandard.Fasbasu]: 'FASBASU',
    [AccountingStandard.TaxAccounting]: 'TaxAccounting',
  },
  inventory_separation: {
    [InventorySeparation.Source]: 'Source',
    [InventorySeparation.Universal]: 'Universal',
  },
  cost_tracking: {
    [CostTracking.Fifo]: 'FIFO',
    [CostTracking.Lifo]: 'LIFO',
    [CostTracking.Hifo]: 'HIFO',
    [CostTracking.Wac]: 'WeightedAverageCost',
  },
  cost_basis_carryover: {
    [CostBasisCarryover.Sell]: 'Sell',
    [CostBasisCarryover.Transfer]: 'Transfer',
  },
  missing_cost_basis: {
    [MissingCostBasis.Dispose]: 'Dispose',
    [MissingCostBasis.Retroactive]: 'Retroactive',
    [MissingCostBasis.Ignore]: 'Ignore',
  },
  base_fiat_currency: cosmicSupportedCurrencies.map((currency) => [currency, currency] as const),
} as const

export type ConfigProp = keyof typeof ACCOUNTING_CONFIG_OPTIONS

const useAccountingSettingsData = ({
  bookType,
  isEnabled = true,
}: { bookType?: BookType; isEnabled?: boolean } = {}) => {
  const { fetch_anchor_swaps, fetch_org_accounting_config } = useApi()
  const { hasPermission } = useUser()

  const anchorSwapsQueryKey = useOrgQueryKey(QueryKey.AnchorSwaps)
  const accountingConfigQueryKey = useOrgQueryKey(QueryKey.AccountingConfig)

  const userHasPermission = hasPermission(Permission.SettingConfigRead)

  const {
    isLoading: isAnchorSwapsLoading,
    isRefetching: isAnchorSwapsRefetching,
    isError: isAnchorSwapsError,
    data: anchorSwaps,
  } = useQuery(anchorSwapsQueryKey, fetch_anchor_swaps, {
    enabled: isEnabled && userHasPermission,
  })

  const {
    isLoading: isAccountingConfigLoading,
    isRefetching: isAccountingConfigRefetching,
    isError: isAccountingConfigError,
    data: accountingConfig,
  } = useQuery(
    [...accountingConfigQueryKey, bookType],
    () => fetch_org_accounting_config({ book_type: bookType }),
    {
      enabled: isEnabled && userHasPermission,
    }
  )

  const isLoading = isAnchorSwapsLoading || isAccountingConfigLoading
  const isError = isAnchorSwapsError || isAccountingConfigError
  const isRefetching = isAnchorSwapsRefetching || isAccountingConfigRefetching

  return {
    isLoading,
    isError,
    isRefetching,
    anchorSwaps,
    accountingConfig: accountingConfig?.result,
  }
}

type UseAccountingSettingsMutationArgs<TData, TVariables> = Pick<
  MutationOptions<TData, Error, TVariables>,
  'mutationFn' | 'onError' | 'onSuccess'
> & { bookType?: BookType }

export function useAccountingSettingsMutation<TData, TVariables>({
  mutationFn,
  onSuccess,
  onError,
  bookType,
}: UseAccountingSettingsMutationArgs<TData, TVariables>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn,
    onError,
    onSuccess: (data, variables, context) => {
      onSuccess?.(data, variables, context)

      void queryClient.invalidateQueries([QueryKey.AnchorSwaps])
      void queryClient.invalidateQueries([QueryKey.AccountingConfig, bookType])
    },
  })
}

export function useUpdateAccountingConfig({
  onSuccess,
  onError,
  bookType,
}: Pick<
  UseAccountingSettingsMutationArgs<
    FetchAccountingSettingsConfigResponse[],
    { items: Array<[string, string | boolean]> }
  >,
  'onSuccess' | 'onError' | 'bookType'
>) {
  const { update_org_accounting_config } = useApi()
  const queryClient = useQueryClient()
  const { setPendingChangeFlag } = useOptimisticPendingChanges()

  return useAccountingSettingsMutation({
    mutationFn: async ({ items }) =>
      Promise.all(
        items.map(([key, value]) => {
          return update_org_accounting_config(key, value, bookType)
        })
      ),
    onSuccess: (data, variables, context) => {
      void queryClient.invalidateQueries([QueryKey.AccountingConfig])
      onSuccess?.(data, variables, context)
      setPendingChangeFlag('config_update')
    },
    onError,
    bookType,
  })
}

export function useUpdateAnchorSwaps({
  onSuccess,
  onError,
}: Pick<
  UseAccountingSettingsMutationArgs<FetchAnchorSwapsResponse, { shouldUseAnchorSwaps: boolean }>,
  'onSuccess' | 'onError'
>) {
  const { update_anchor_swaps } = useApi()

  return useAccountingSettingsMutation({
    mutationFn: async ({ shouldUseAnchorSwaps }) => update_anchor_swaps(shouldUseAnchorSwaps),
    onSuccess,
    onError,
  })
}

export default useAccountingSettingsData
